import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, message } from 'antd';
import { useCampusBranches } from '@hooks/tenant/useCampusBranches';
import { useStudentBadges } from '@hooks/tenant/useStudentBadges';
import { useCreateStudentBadge } from '@hooks/tenant/useCreateStudentBadge';
import { STUDENT_BADGES_DEFAULT_BRANCH_ID } from '@constants/firestorePaths';

/**
 * Badge management tab: campus filter (same ids as School Time / classes), table, create modal.
 */
export function useBadgeManagement({ tenantId }) {
    const { branches } = useCampusBranches({ tenantId });
    const campusPickList = useMemo(
        () => branches.filter((b) => (b.branchName || '').trim()),
        [branches]
    );
    const branchOptions = useMemo(
        () => campusPickList.map((b) => ({ value: b.id, label: b.branchName || b.id })),
        [campusPickList]
    );
    const branchNameById = useMemo(() => {
        const m = new Map();
        m.set(STUDENT_BADGES_DEFAULT_BRANCH_ID, 'Default');
        for (const b of branches) m.set(b.id, (b.branchName || '').trim() || b.id);
        return m;
    }, [branches]);

    const [badgeBranchTableFilter, setBadgeBranchTableFilter] = useState('__all__');
    const [createModalBranchId, setCreateModalBranchId] = useState(null);

    useEffect(() => {
        if (badgeBranchTableFilter !== '__all__' && !campusPickList.some((b) => b.id === badgeBranchTableFilter)) {
            setBadgeBranchTableFilter('__all__');
        }
    }, [campusPickList, badgeBranchTableFilter]);

    const hasCampuses = campusPickList.length > 0;

    const effectiveBranchIdForData = useMemo(() => {
        if (!hasCampuses) return STUDENT_BADGES_DEFAULT_BRANCH_ID;
        if (badgeBranchTableFilter === '__all__') return undefined;
        return badgeBranchTableFilter;
    }, [hasCampuses, badgeBranchTableFilter]);

    const { badgeRows, loading, summary, badgeYearDefault } = useStudentBadges({
        tenantId,
        branchId: effectiveBranchIdForData,
    });
    const { badgeRows: allBadges } = useStudentBadges({ tenantId });

    const createBadgeMutation = useCreateStudentBadge({ tenantId });

    const [badgeCreateForm] = Form.useForm();
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const nextBadgeNumberPreview = useMemo(() => {
        if (!hasCampuses) {
            return (Number(summary?.lastBadgeNumber) || 0) + 1;
        }
        if (badgeBranchTableFilter !== '__all__') {
            return (Number(summary?.lastBadgeNumber) || 0) + 1;
        }
        const bid = createModalBranchId;
        if (!bid) return 1;
        const subset = badgeRows.filter((r) => r.branchId === bid);
        const maxNum = subset.reduce((m, r) => Math.max(m, Number(r.badgeNumber) || 0), 0);
        return maxNum + 1;
    }, [hasCampuses, badgeBranchTableFilter, summary, createModalBranchId, badgeRows]);

    const badgeColumns = useMemo(() => {
        const cols = [
            {
                title: 'Title',
                dataIndex: 'title',
                key: 'title',
                render: (v) => (v || '').trim() || 'Untitled',
            },
            ...(hasCampuses && badgeBranchTableFilter === '__all__'
                ? [
                    {
                        title: 'Campus',
                        dataIndex: 'branchId',
                        key: 'branchId',
                        width: 160,
                        render: (id) => (id ? branchNameById.get(id) || '—' : '—'),
                    },
                ]
                : []),
            { title: 'Badge #', dataIndex: 'badgeNumber', key: 'badgeNumber', width: 96, render: (v) => v ?? '—' },
            { title: 'Year', dataIndex: 'year', key: 'year', width: 88, render: (v) => v ?? '—' },
            {
                title: 'Students',
                dataIndex: 'assignedStudentCount',
                key: 'assignedStudentCount',
                width: 110,
                render: (n) => Number(n) || 0,
            },
        ];
        return cols;
    }, [hasCampuses, badgeBranchTableFilter, branchNameById]);

    const openCreateModal = useCallback(() => {
        const initialBranch =
            !hasCampuses
                ? STUDENT_BADGES_DEFAULT_BRANCH_ID
                : campusPickList.length === 1
                  ? campusPickList[0].id
                  : badgeBranchTableFilter !== '__all__'
                    ? badgeBranchTableFilter
                    : undefined;
        badgeCreateForm.setFieldsValue({ title: '', year: badgeYearDefault, branchId: initialBranch });
        setCreateModalBranchId(initialBranch ?? null);
        setCreateModalOpen(true);
    }, [badgeCreateForm, badgeYearDefault, campusPickList, hasCampuses, badgeBranchTableFilter]);

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        setCreateModalBranchId(null);
        badgeCreateForm.resetFields();
    }, [badgeCreateForm]);

    const onBadgeCreateFormValuesChange = useCallback((_, all) => {
        const bid = all?.branchId;
        if (bid !== undefined) setCreateModalBranchId(bid || null);
    }, []);

    const submitCreateModal = useCallback(async () => {
        try {
            const v = await badgeCreateForm.validateFields();
            const branchForCreate = !hasCampuses
                ? STUDENT_BADGES_DEFAULT_BRANCH_ID
                : v.branchId;
            if (!branchForCreate) {
                message.error('Select a campus for this badge');
                return;
            }
            await createBadgeMutation.mutateAsync({ title: v.title, year: v.year, branchId: branchForCreate });
            message.success('Badge created');
            setCreateModalOpen(false);
            setCreateModalBranchId(null);
            badgeCreateForm.resetFields();
        } catch (e) {
            if (e?.message !== 'Validation failed') message.error(e?.message || 'Failed to create badge');
        }
    }, [badgeCreateForm, createBadgeMutation, hasCampuses]);

    return {
        badgeRows,
        allBadges,
        loading,
        badgeColumns,
        nextBadgeNumberPreview,
        badgeYearDefault,
        badgeCreateForm,
        createModalOpen,
        openCreateModal,
        closeCreateModal,
        submitCreateModal,
        isCreating: createBadgeMutation.isPending,
        campusPickList,
        branchOptions,
        badgeBranchTableFilter,
        setBadgeBranchTableFilter,
        hasCampuses,
        onBadgeCreateFormValuesChange,
        createModalRequiresBranchPick: hasCampuses,
    };
}
